<template>
  <div class="wrapper">
    <div class="animated fadeIn">
      <b-card>
        <b-row class="mb-3">
          <b-col>
            <h4>{{ $t('nav.detections_list_title') }}</h4>
          </b-col>
          <b-col md="auto">
            <b-button-group>
              <CreateSamplesFromDetections :detection-set-id="detectionSetId" :filters="filters" ref="createSamplesPopup" :selectedIds="selectedIds" @created="resetSelected"
                :total="totalSelected"/>
            </b-button-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <DebouncedSearch v-model="filters.search" @click="getData()"/>
          </b-col>
          <b-col md="auto">
            <b-form-group label-cols :label="$dt('global.per_page', 'Per page:')">
              <b-form-select :options="[2, 10, 20, 50, 100, 500]" v-model="filters.per_page" />
            </b-form-group>
          </b-col>
          <b-col md="auto" v-if="listPaginator.last_page > 1">
            <Pagination :data="listPaginator" :limit="5" @pagination-change-page="onPageChange" />
          </b-col>
        </b-row>
        <JsonFilters ref="jsonFilters" :detection-set-id="detectionSetId" @updated="onFilterUpdated"/>
        <div v-if="loading" class="text-center">
          <b-spinner type="grow" variant="primary" />
        </div>
        <div v-else>
          <b-row>
            <b-col>
              <NoDataView v-if="!tableItems.length" />
              <DetectionsTableView ref="tableView" v-else-if="isListView" @sendForDelete="sendForDelete" :tableFields="tableFields"
                :filters="filters" :detections="tableItems" @selected="val => selectedIds = val"/>
              <DetectionsThumbnailView v-else @sendForDelete="sendForDelete" :detections="tableItems" />
              <Delete ref="triggerDelete" :id="this.detectionId" @deleted="getData" style="display: none"
                :deleteFn="() => deleteFn(this.activeProjectId, this.detectionSetId, this.detectionId)">
              </Delete>
            </b-col>
          </b-row>
          <b-row>
            <!--Paginator at bottom-->
            <b-col v-if="tableItems.length">
              <Pagination :data="listPaginator" :limit="5" @pagination-change-page="onPageChange" />
            </b-col>
          </b-row>
        </div>
      </b-card>
      <SelectedDetectionsActions v-if="selectedIds.length > 0" :selected-ids="selectedIds" @create="$refs.createSamplesPopup.showModal" @delete="bulkDelete" @clear="resetSelected"/>
    </div>
  </div>
</template>

<script>
import Pagination from 'laravel-vue-pagination';
import { mapState } from 'vuex';
import DetectionsTableView from '@/views/Detections/DetectionsTableView.vue';
import DetectionsThumbnailView from '@/views/Detections/DetectionsThumbnailView.vue';
import NoDataView from '@/views/Shared/NoDataView.vue';
import Delete from '@/views/Shared/ModalDelete.vue';
import Services from '@/services/index';
import { fileIconMap } from '@/shared/classes';
import JsonFilters from '@/views/Shared/JsonFilters.vue';
import CreateSamplesFromDetections from '@/views/Detections/CreateSamplesFromDetections.vue';
import SelectedDetectionsActions from '@/views/Detections/SelectedDetectionsActions.vue';
import DebouncedSearch from '../Shared/DebouncedSearch.vue';

export default {
  mounted() {
    this.$refs.jsonFilters.loadFilters();
    // this.getData();
  },
  props: {
    page: Number,
    detectionSetId: {
      type: Number,
      required: true
    },
  },
  data() {
    return {
      fileIconMap,
      tableFields: [
        { key: 'id', label: this.$t('global.id') },
        { key: 'files', label: this.$t('detection.files'), thClass: 'file-column' },
        { key: 'parsed_data', label: this.$dt('detection.parsed_data', 'Parsed Data') },
        { key: 'options', label: this.$t('global.options') },
        { key: 'select', label: this.$dt('global.select_detections', 'Select') }
      ],
      filters: {
        sort_field: null,
        sort_order: null,
        search: '',
        per_page: 20,
      },
      loading: false,
      list: {},
      isListView: true,
      detectionId: null,
      selectedIds: [],
    };
  },
  components: {
    Pagination,
    NoDataView,
    Delete,
    DetectionsTableView,
    DetectionsThumbnailView,
    JsonFilters,
    CreateSamplesFromDetections,
    SelectedDetectionsActions,
    DebouncedSearch,
  },
  computed: {
    listPaginator() {
      return Object.is(this.list.meta, undefined) ? {} : this.list.meta;
    },
    tableItems() {
      return this.list.data || [];
    },
    params() {
      return {
        ...this.filters,
        page: this.page,
        sort_order: this.filters.sort_order ? 'desc' : 'asc'
      };
    },
    totalSelected() {
      return this.selectedIds.length > 0 ? this.selectedIds.length : (this.listPaginator?.total || 0);
    },
    ...mapState('Auth', ['activeProjectId', 'token']),
  },
  watch: {
    page: 'getData',
    filters: {
      deep: true,
      handler: function () {
        this.resetSelected();
        this.getData();
      }
    },
  },
  methods: {
    getData() {
      this.loading = true;
      Services.detections.list(this.activeProjectId, this.detectionSetId, this.params).then(({ data }) => {
        this.list = data;
        this.loading = false;
      }).catch((err) => {
        if (err.code !== 'ERR_CANCELED') {
          this.loading = false;
          console.log(err);
        }
      });
    },
    onFilterUpdated(filters) {
      this.filters = {
        ...this.filters,
        json_filters: filters,
      };
    },
    onPageChange(page) {
      page = page || this.page;
      if (page !== this.page) this.$router.push({ name: 'Detections', params: { page } });
    },
    sendForDelete(id) {
      this.detectionId = id;
      this.$refs.triggerDelete.onShowModal();
    },
    deleteFn(projectId, detectionSetId, id) {
      return Services.detections.delete(projectId, detectionSetId, id);
    },
    resetSelected() {
      this.selectedIds = [];
      if (this.$refs.tableView) {
        this.$refs.tableView.clearSelected();
      }
    },
    bulkDelete(ids) {
      Services.detections.deleteIds(this.activeProjectId, this.detectionSetId, { ids }).then(() => {
        this.getData();
        this.resetSelected();
      }).catch((err) => {
        console.log(err);
      });
    },
  }
};
</script>

<style scoped lang="scss">
/deep/ .file-column {
  width: 200px;
}
</style>
